import React from 'react';
import Link from 'next/link';
import { Tooltip } from '@chakra-ui/react';
import { parseISO } from 'date-fns';
import { isEmpty } from 'lodash';
import { Icon, IconProps, IconSize } from '@/components/common/Icon';
import { useTranslations } from '@/core/translations';
import { Formatter } from '@/core/utils/Formatter';
import { Colors } from '@/styles/colors';
import { useUserIncidentsColorSchemes } from '@/components/pages/SituationalAwareness/hooks/useUserIncidentsColorSchemes';
import styles from './TargetDetails.module.scss';

const formatter = new Formatter();

export interface TargetDetailsProps {
  incidentMarkerColor?: string;
  incidentTypeName?: string;
  severityName?: string;
  dataSource?: string;
  date?: string;
  addressName?: string;
  coords?: { lat: number; lng: number };
  contact?: Partial<{ email: string; name: string; phone: string }>;
  sourceLink?: string;
}

const getCoords = (coords: TargetDetailsProps['coords']) => `${coords?.lat.toFixed(6)}, ${coords?.lng.toFixed(6)}`;
const getTimeDate = (date: string) => formatter.date.format(parseISO(date));

const CellItem: React.FC<
  React.PropsWithChildren<{
    iconName?: IconProps['name'];
    incidentColor?: TargetDetailsProps['incidentMarkerColor'];
    iconProps?: Omit<IconProps, 'name'>;
    tooltipLabel?: string;
  }>
> = ({ iconName, incidentColor, iconProps, tooltipLabel, children }) => (
  <div className={styles.cell}>
    {incidentColor && <div className={styles.marker} style={{ background: incidentColor }} />}
    {iconName &&
      (tooltipLabel ? (
        <Tooltip label={tooltipLabel} placement="top">
          <span tabIndex={0}>
            <Icon name={iconName} size={IconSize.s12} fillColor={Colors.Neutral} {...iconProps} />
          </span>
        </Tooltip>
      ) : (
        <Icon name={iconName} size={IconSize.s12} fillColor={Colors.Neutral} {...iconProps} />
      ))}
    {children}
  </div>
);

export const TargetDetails: React.FC<TargetDetailsProps> = ({
  incidentMarkerColor,
  incidentTypeName,
  severityName,
  dataSource,
  date,
  addressName,
  coords,
  contact,
  sourceLink,
}) => {
  const t = useTranslations('incidents_page.target_card');
  const { schemaKey } = useUserIncidentsColorSchemes();

  return (
    <div className={styles.container}>
      {incidentTypeName && (
        <CellItem incidentColor={schemaKey === 'IncidentTypeID' ? incidentMarkerColor : undefined}>
          <div>{incidentTypeName}</div>
        </CellItem>
      )}
      {severityName && (
        <CellItem
          iconName="WarningIcon"
          tooltipLabel={t('severity-or-impact')}
          incidentColor={schemaKey === 'SeverityLevel' ? incidentMarkerColor : undefined}
        >
          <div>{severityName}</div>
        </CellItem>
      )}
      {dataSource && (
        <CellItem iconName="WebTagIcon" tooltipLabel={t('source')}>
          <div>{dataSource}</div>
          {sourceLink && (
            <Link className={styles.link} href={sourceLink} target="_blank">
              <Icon name="LaunchIcon" size={12} />
            </Link>
          )}
        </CellItem>
      )}
      {date && (
        <CellItem iconName="CalendarIcon" tooltipLabel={t('date-and-time')}>
          <div>{getTimeDate(date)}</div>
        </CellItem>
      )}
      {addressName && (
        <CellItem iconName="LocationIcon" tooltipLabel={t('location')}>
          <div>{addressName}</div>
        </CellItem>
      )}
      {!isEmpty(coords) && (
        <CellItem iconName="CoordinatesIcon" tooltipLabel={t('gps-coordinates')}>
          <div>{getCoords(coords)}</div>
        </CellItem>
      )}
      {!isEmpty(contact) && (
        <CellItem iconName="ContactIcon" tooltipLabel={t('contact')}>
          <div>{Object.entries(contact).map(([key, value]) => value && <div key={key}>{value}</div>)}</div>
        </CellItem>
      )}
    </div>
  );
};
